<template>
    <div>
        <b-row class="mb-2">
            <b-col>
                <label>
                    {{ label }}
                    <slot name="help-text"></slot>
                </label>
                <a href="#"
                    v-if="showAssignToMeLink && (!localAssignment || localAssignment.id?.toString().toLowerCase() != $user.userName.toLowerCase())"
                    @click.prevent="assignToMe" class="pull-right mt-1">
                    <i class="fa fa-user-check" />
                    Assign to Me
                </a>
                <b-form-group>
                    <multiselect v-model="localAssignment" :options="assignmentOptions" :multiple="false"
                        group-values="options" group-label="type" label="name" track-by="id"
                        :disabled="disabled" @input="emitAssignmentChange" placeholder="Select assignment...">

                        <template #option="data">
                            <i v-if="data.option.isTeam" class="fa fa-users"></i>
                            <i v-else class="fa fa-user"></i>
                            {{ data.option.$groupLabel }}
                            {{ data.option.name }}
                        </template>

                        <template #singleLabel="data">
                            <b-badge v-if="data.option.isTeam" variant="primary" class="mr-1 my-0">
                                <i class="fa fa-users"></i>
                                {{ data.option.name }}
                            </b-badge>
                            <b-badge v-else variant="info" class="mr-1 my-0">
                                <i class="fa fa-user"></i>
                                {{ data.option.name }}
                            </b-badge>
                        </template>
                    </multiselect>
                </b-form-group>
            </b-col>
        </b-row>
    </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import Multiselect from 'vue-multiselect';
import { Team } from '@/models/Team';
import { User } from '@/models/User';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

interface AssignmentOption {
    id: string | number;
    name: string;
    isTeam?: boolean;
}

interface AssignmentData {
    teamId?: number;
    userName?: string;
    teamName?: string;
}

@Component({
    name: "CaseAssignmentComponent",
    components: {
        Multiselect
    }
})
export default class CaseAssignmentComponent extends Vue {
    @Prop({ default: null }) value!: AssignmentData | null;
    @Prop({ type: Boolean, default: false }) disabled!: boolean;
    @Prop({ type: Boolean, default: true }) showAssignToMeLink!: boolean;
    @Prop({ type: String, default: 'Assignment' }) label!: string;

    protected teams: Team[] = [];
    protected users: User[] = [];
    private localAssignment: AssignmentOption | null = null;
    private isInitializing = true;

    async mounted() {
        try {
            await this.loadTeamsAndUsers();
            this.initializeAssignment();
            this.isInitializing = false;
        } catch (error) {
            console.error('Error in CaseAssignmentComponent mounted:', error);
            this.isInitializing = false;
        }
    }

    async loadTeamsAndUsers() {
        try {
            await Promise.all([
                this.getTeams(),
                this.getUsers()
            ]);
        } catch (error) {
            console.error('Error loading teams and users:', error);
        }
    }

    async getTeams() {
        try {
            const response = await this.$http.get<Team[]>("/user/teams");
            this.teams = response.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    async getUsers() {
        try {
            const response = await this.$http.get<User[]>("/user");
            this.users = response.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    private initializeAssignment() {
        if (this.value) {
            this.localAssignment = {
                id: this.value.teamId ? this.value.teamId : this.value.userName!,
                name: this.value.teamId ? this.value.teamName! : this.value.userName!,
                isTeam: !!this.value.teamId
            };
        } else {
            this.localAssignment = null;
        }
    }

    get assignmentOptions() {
        return [
            {
                type: 'Teams',
                options: this.teams.map(t => ({
                    id: t.id,
                    name: t.name,
                    isTeam: true
                }))
            },
            {
                type: 'Users',
                options: this.users.map(u => ({
                    id: u.userName,
                    name: u.userName,
                    isTeam: false
                }))
            }
        ]
    }

    @Watch('value', { deep: true })
    onValueChanged() {
        if (this.isInitializing) {
            return;
        }
        this.initializeAssignment();
    }

    private emitAssignmentChange() {
        if (!this.localAssignment) {
            this.$emit('input', null);
            return;
        }

        const assignment: AssignmentData = {
            teamId: this.localAssignment.isTeam ? this.localAssignment.id as number : undefined,
            userName: !this.localAssignment.isTeam ? this.localAssignment.id as string : undefined,
            teamName: this.localAssignment.isTeam ? this.localAssignment.name : undefined
        };

        this.$emit('input', assignment);
    }

    assignToMe() {
        const userName = this.$user?.userName;
        if (!userName) return;

        this.localAssignment = {
            id: userName,
            name: userName,
            isTeam: false
        };

        // Manually emit after user action
        this.emitAssignmentChange();
    }
}
</script>

<style scoped>
@import "vue-multiselect/dist/vue-multiselect.min.css";

/* Match the badge styling of CasesWatcherComponent #tag slot */
::v-deep .multiselect__single {
    padding: 0;
    margin: 0;
    min-height: 0;
    line-height: 1;
}

::v-deep .multiselect__single .badge {
    font-size: 0.875rem;
    padding: 0.25rem 0.5rem;
}
</style>
